import { useEffect } from 'react';
import { updateMetaTags, resetMetaTags } from '../utils/metaTags';

/**
 * Custom React hook for managing meta tags
 * @param {Object} config - Meta tag configuration
 * @param {string} config.title - Page title
 * @param {string} config.description - Page description
 * @param {string} config.image - OG image URL
 * @param {string} config.url - Page URL
 * @param {string} config.type - og:type value
 * @param {boolean} config.resetOnUnmount - Reset tags on component unmount (default: false)
 *
 * @example
 * // Basic usage
 * useMetaTags({
 *   title: 'About Us',
 *   description: 'Learn about our company',
 * });
 *
 * @example
 * // With URL and image
 * useMetaTags({
 *   title: 'Projects | build:base',
 *   description: 'View our latest projects',
 *   image: '/images/projects-og.png',
 *   url: '/projects',
 * });
 *
 * @example
 * // Reset on unmount
 * useMetaTags({
 *   title: 'Modal Content',
 *   description: 'Temporary page content',
 *   resetOnUnmount: true,
 * });
 */
export const useMetaTags = (config = {}, resetOnUnmount = false) => {
  useEffect(() => {
    if (config && Object.keys(config).length > 0) {
      updateMetaTags(config);
    }

    // Cleanup function to reset meta tags when component unmounts
    return () => {
      if (resetOnUnmount) {
        resetMetaTags();
      }
    };
  }, [config, resetOnUnmount]);
};

export default useMetaTags;
